import { AsyncPipe, NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from "@angular/core";
import { ChildActivationEnd, NavigationEnd, Router, RouterLink, RouterOutlet } from "@angular/router";
import { AccountMarketplace, User } from "@front/m19-api-client";
import { OrganizationAccountGroups } from "@front/m19-models";
import {
  AccountSelectionService,
  AuthService,
  CurrencyService,
  Hotkeys,
  NotificationService,
  UserSelectionService,
} from "@front/m19-services";
import {
  IButtonDirective,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  ITab,
  ITabsComponent,
  IToasterListComponent,
  ModalService,
  IIconComponent,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import { LogoComponent } from "@m19-board/shared/logo/logo.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { BoardDebugInfoModalComponent } from "../board-layout/board-debug-info-modal.component";
import { PageLayoutComponent } from "../page-layout/page-layout.component";

@UntilDestroy()
@Component({
  templateUrl: "./dashboard360-layout.component.html",
  imports: [
    AsyncPipe,
    RouterLink,
    TranslocoDirective,
    IButtonDirective,
    RouterOutlet,
    LogoComponent,
    IToasterListComponent,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
    ITabsComponent,
    PageLayoutComponent,
    NgClass,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Dashboard360LayoutComponent implements OnInit {
  private authService = inject(AuthService);
  private userSelectionService = inject(UserSelectionService);
  currencyService = inject(CurrencyService);
  router = inject(Router);
  private layoutSelector = inject(LayoutSelectorService);
  private notificationService = inject(NotificationService);
  private accountSelectionService = inject(AccountSelectionService);
  private modalService = inject(ModalService);
  private hotkeys = inject(Hotkeys);
  private translocoService = inject(TranslocoService);

  isTest = !environment.production;
  readonly tabs: ITab<string>[] = [
    { value: "account360", label: "Accounts 360", url: "/dashboard360/account360", testId: "tabbed-nav-Accounts_360" },
    { value: "product360", label: "Product 360", url: "/dashboard360/product360", testId: "tabbed-nav-Product_360" },
  ];
  activeTab = signal<string>("account360");

  // input/app state
  user?: User;
  locale?: string;
  boardType?: BoardType;
  globalWarning$: Observable<string>;
  accountMarketplaces?: AccountMarketplace[];
  selectedDateRage?: string[];

  // component state
  displaySettingsMenu = false;
  debugInfoModalVisible = false;
  url: string = "";

  constructor() {
    const router = this.router;

    this.globalWarning$ = this.notificationService.globalWarning$;
    this.hotkeys
      .addShortcut({ altKey: "KeyD" })
      .pipe(untilDestroyed(this))
      .subscribe(() => this.printDebugInfo());
    this.url = router.url;
    router.events.pipe(untilDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationEnd || event instanceof ChildActivationEnd) {
        this.url = router.url;
        this.activeTab.set(this.tabs.find((t) => router.url.includes(t.value))?.value ?? "account360");
      }
    });
  }

  ngOnInit(): void {
    this.boardType = this.layoutSelector.getBoardType();
    this.accountSelectionService.accountMarketplacesSelection$
      .pipe(untilDestroyed(this))
      .subscribe((accountMarketplaces) => {
        this.accountMarketplaces = accountMarketplaces;
      });

    this.authService.loggedUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.user = user;
      this.locale = user.locale;
    });
    this.userSelectionService.dateRange$.pipe(untilDestroyed(this)).subscribe((dr) => {
      this.selectedDateRage = dr;
    });
  }

  selectOrganization(organizations: OrganizationAccountGroups[]) {
    this.userSelectionService.setSelectedOrganizations(organizations);
  }

  isAdminLogin(): boolean {
    return this.authService.isAdminLogin();
  }

  printDebugInfo() {
    // prevent modal to open again if already shown
    if (this.debugInfoModalVisible) {
      return;
    }
    this.debugInfoModalVisible = true;
    const modalRef = this.modalService.openModal(BoardDebugInfoModalComponent, {});

    modalRef.afterClosed().subscribe(() => {
      this.debugInfoModalVisible = false;
    });
  }

  logout(): void {
    this.authService.logout();
  }

  toggleSettings(): void {
    this.displaySettingsMenu = !this.displaySettingsMenu;
  }
}
